import { ReactNode, AnchorHTMLAttributes, ButtonHTMLAttributes } from 'react';

type BaseProps = {
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'accent' | 'destructive' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  className?: string;
};

type LinkProps = BaseProps & 
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof BaseProps> & {
    href: string;
  };

type ButtonProps = BaseProps & 
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseProps> & {
    href?: never;
  };

type LinkButtonProps = LinkProps | ButtonProps;

export function LinkButton({
  children,
  variant = 'primary',
  size = 'sm',
  fullWidth = false,
  className = '',
  ...props
}: LinkButtonProps): React.JSX.Element {  
  const baseStyles = [
    'inline-flex',
    'items-center',
    'justify-center',
    'transition-all',
    'duration-200',
    'cursor-pointer',
    'border',
    'no-underline',
    fullWidth ? 'w-full' : '',
  ].filter(Boolean).join(' ');

  // Size styles using CSS variables
  const sizeStyles = {
    sm: 'px-[var(--spacing-3)] py-[var(--spacing-1)] rounded-[var(--radius)]',
    md: 'px-[var(--spacing-4)] py-[var(--spacing-2)] rounded-[var(--radius)]',
    lg: 'px-[var(--spacing-5)] py-[var(--spacing-3)] rounded-[var(--radius)]',
  };

  // Variant styles using CSS variables
  const getVariantStyles = (): 
  { 
    className: string,
    style: {
      backgroundColor?: string;
      color?: string;
      borderColor?: string;
    } | undefined;
  } => {
    switch (variant) {
    case 'primary':
      return {
        className: 'border-primary hover:opacity-90',
        style: {
          backgroundColor: 'var(--primary)',
          color: 'var(--primary-foreground)',
          borderColor: 'var(--primary)',
        },
      };
    case 'secondary':
      return {
        className: 'border-border hover:opacity-90',
        style: {
          backgroundColor: 'var(--button-active)',
          color: 'var(--high-emphasis-font)',
          borderColor: 'var(--border)',
        },
      };
    case 'accent':
      return {
        className: 'border-accent hover:opacity-90',
        style: {
          backgroundColor: 'var(--accent)',
          color: 'var(--accent-foreground)',
          borderColor: 'var(--accent)',
        },
      };
    case 'destructive':
      return {
        className: 'border-destructive hover:opacity-90',
        style: {
          backgroundColor: 'var(--destructive)',
          color: 'var(--destructive-foreground)',
          borderColor: 'var(--destructive)',
        },
      };
    case 'ghost':
      return {
        className: 'border-transparent hover:opacity-70',
        style: {
          backgroundColor: 'transparent',
          color: 'var(--high-emphasis-font)',
          borderColor: 'transparent',
        },
      };
    default:
      return {
        className: '',
        style: {},
      };
    }
  };

  const variantConfig = getVariantStyles();
  const combinedClassName = [
    baseStyles,
    sizeStyles[size],
    variantConfig.className,
    className,
  ].filter(Boolean).join(' ');

  if ('href' in props && props.href) {
    return (
      <a
        {...(props as AnchorHTMLAttributes<HTMLAnchorElement>)}
        className={combinedClassName}
        style={{
          ...variantConfig.style,
          fontSize: 'var(--text-h4)',
          ...(props.style || {}),
        }}
      >
        {children}
      </a>
    );
  }

  return (
    <button
      {...(props as ButtonHTMLAttributes<HTMLButtonElement>)}
      className={combinedClassName}
      style={{
        ...variantConfig.style,
        ...(props.style || {}),
      }}
    >
      {children}
    </button>
  );
}
